import { Link } from "react-router-dom";
import { ArrowRight, Phone, MapPin, Building2, Car, FileText, Users, Info } from "lucide-react";
import PageShell from "@/components/layout/PageShell";
import Reveal from "@/components/shared/Reveal";
import { BRAND } from "@/lib/data";

const SECTIONS = [
  {
    icon: MapPin,
    title: "Pick-up location & operating hours",
    body: "Before you travel, check your booking confirmation for your designated pick-up location, rental office operating hours, and arrival and collection instructions.",
    bullets: [
      "Your designated pick-up location",
      "Rental office operating hours",
      "Arrival and collection instructions",
    ],
  },
  {
    icon: Building2,
    title: "Airport pick-ups",
    body: "Depending on the airport, your rental counter may be conveniently located within the terminal, or accessible via a complimentary shuttle service.",
    bullets: [
      "Located within the terminal, or",
      "Accessible via a complimentary shuttle service",
    ],
    note:
      "To avoid additional fees or delays, arrive as close as possible to your scheduled pick-up time. Some rental providers may apply charges for early or late vehicle collection.",
  },
  {
    icon: Car,
    title: "Vehicle allocation",
    body:
      "Your reservation guarantees a vehicle within the selected category or class. While the exact make or model cannot always be guaranteed, you'll receive a comparable vehicle with similar features and specifications.",
  },
  {
    icon: FileText,
    title: "Documents required at pick-up",
    body: "Please ensure you have the following original documents available:",
    bullets: [
      "Valid Driver's Licence",
      "International Driving Permit (IDP) — required in certain countries or when your licence is not issued in the Roman alphabet",
      "Credit Card in the primary driver's name for the security deposit",
      "Proof of Insurance — only if you intend to use your personal motor insurance or credit card coverage",
    ],
    note:
      "Failure to present the required documents may result in the rental company being unable to release the vehicle.",
  },
  {
    icon: Users,
    title: "Driver requirements",
    body: "If more than one person will be driving, please note:",
    bullets: [
      "Additional drivers must be registered on the rental agreement",
      "Extra driver fees may apply, depending on the rental company",
    ],
    secondaryTitle: "Every driver must",
    secondaryBullets: [
      "Hold a valid driving licence",
      "Have been licensed for at least one year",
      "Meet the rental company's minimum age requirements",
    ],
    note:
      "For destination-specific policies, age restrictions, or additional driver charges, please refer to the Rental Terms & Conditions included with your booking or contact the rental provider directly.",
  },
];

export default function PickupGuide() {
  return (
    <PageShell>
      {/* Hero */}
      <section className="bg-[#0B2545] text-white pt-16 md:pt-24 pb-14 md:pb-20" data-testid="pickup-guide-hero">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10">
          <div className="overline-light mb-6">Pick-Up Guide</div>
          <h1 className="font-serif text-[9vw] md:text-[6vw] lg:text-[5vw] leading-[1.02] tracking-[-0.02em] text-white max-w-5xl">
            Everything you need for a smooth pick-up experience.
          </h1>
          <p className="mt-8 text-white/85 text-lg md:text-xl leading-relaxed max-w-3xl">
            A little preparation goes a long way. Review the information below before collecting
            your rental vehicle to ensure a quick and hassle-free pick-up.
          </p>
        </div>
      </section>

      {/* Sections */}
      <section className="py-16 md:py-24 bg-white">
        <div className="mx-auto max-w-[1400px] px-5 md:px-10 space-y-8">
          {SECTIONS.map((s, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <article className="card-flat p-8 md:p-10" data-testid={`pickup-section-${i}`}>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-start">
                  <div className="md:col-span-3 flex flex-col items-start gap-4">
                    <div className="h-12 w-12 bg-[#0B2545] text-white flex items-center justify-center">
                      <s.icon className="h-5 w-5" strokeWidth={1.6} />
                    </div>
                    <span className="font-mono text-xs text-[#5B6472]">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                  </div>
                  <div className="md:col-span-9">
                    <h2 className="font-serif text-2xl md:text-3xl text-[#0B2545] leading-tight mb-4">{s.title}</h2>
                    {s.body && <p className="text-[#5B6472] leading-relaxed mb-5">{s.body}</p>}
                    {s.bullets && (
                      <ul className="space-y-2.5 mb-5">
                        {s.bullets.map((b, j) => (
                          <li key={j} className="flex items-start gap-3 text-[#0B2545]">
                            <span className="mt-2 h-1.5 w-1.5 bg-[#0B2545] shrink-0" />
                            <span>{b}</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    {s.secondaryTitle && (
                      <>
                        <div className="overline mb-3">{s.secondaryTitle}</div>
                        <ul className="space-y-2.5 mb-5">
                          {s.secondaryBullets.map((b, j) => (
                            <li key={j} className="flex items-start gap-3 text-[#0B2545]">
                              <span className="mt-2 h-1.5 w-1.5 bg-[#0B2545] shrink-0" />
                              <span>{b}</span>
                            </li>
                          ))}
                        </ul>
                      </>
                    )}
                    {s.note && (
                      <div className="mt-6 flex gap-3 border-l-2 border-[#0B2545] pl-4 py-2 bg-[#F5F7FA]">
                        <Info className="h-4 w-4 text-[#0B2545] shrink-0 mt-0.5" strokeWidth={1.8} />
                        <p className="text-sm text-[#0B2545]">{s.note}</p>
                      </div>
                    )}
                  </div>
                </div>
              </article>
            </Reveal>
          ))}

          {/* Tip callout */}
          <Reveal>
            <div className="bg-[#0B2545] text-white p-8 md:p-10">
              <div className="overline-light mb-3">Tip</div>
              <p className="font-serif text-2xl md:text-3xl leading-tight text-white">
                Carry both digital and printed copies of your booking confirmation for quicker verification at the rental desk.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-24 bg-[#F5F7FA] border-t border-line">
        <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-10 items-center">
          <h2 className="font-serif text-3xl md:text-5xl leading-tight text-[#0B2545]">
            Questions about your pick-up? Call our team.
          </h2>
          <div className="flex flex-col sm:flex-row gap-3">
            <a href={BRAND.phoneHref} className="btn-primary" data-testid="pickup-call">
              <Phone className="h-4 w-4" strokeWidth={2} /> {BRAND.phone}
            </a>
            <Link to="/contact" className="btn-ghost-light">
              Contact us <ArrowRight className="h-4 w-4" strokeWidth={1.8} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}
